import * as THREE from 'three/webgpu';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { FLOOR_HEIGHTS_M, SLAB_M } from '../config';
import type { Plan, PlanFloor, Point, SnapshotUnit } from '../data';
import { curtainWall, edgesOf, inside, runsOf, upstand, type Segment } from './facade';
import { deckTexture, MAT } from './look';
import { furniture, roofPlant, shrubs, signMesh, stoneTexture, tableSpots } from './scenery';

/**
 * مطل, built from the traced plan.
 *
 * Outlines are NAMA's marketing drawing read off a 10 px grid (good to about
 * half a metre); heights are the assumption in config.ts; walls are inferred
 * from which edges face out (facade.ts); every finish, fitting and the order
 * it goes up in is illustrative (scenery.ts, look.ts). A modelled glTF will
 * replace all of it; it should keep one mesh per space named by plan code,
 * which is all the rest of this page relies on.
 */

/**
 * The order the building goes up in, for «مراحل البناء». A guess at the usual
 * sequence for a concrete frame with a curtain wall, since there is no
 * programme for مطل: 1 the ground slab, 2 the frame and cores in raw
 * concrete, 3 the envelope (cladding, glazing, planters), 4 fit-out.
 */
export const STAGES = [
  { key: 'foundation', labelAr: 'الأساسات', noteAr: 'تُصبّ البلاطة الأرضية والحوض المائي.' },
  {
    key: 'structure',
    labelAr: 'الهيكل',
    noteAr: 'أعمدة وبلاطات خرسانية ونوى الخدمات، دورًا فوق دور.',
  },
  {
    key: 'envelope',
    labelAr: 'الواجهات',
    noteAr: 'كسوة حجرية وإطارات داكنة وزجاج وأحواض التراسات.',
  },
  {
    key: 'fitout',
    labelAr: 'التشطيب',
    noteAr: 'الأرضيات والأثاث والإنارة والزراعة واللافتات.',
  },
] as const;

/**
 * What a material is, for the two modes that restyle the whole building: the
 * X-ray keeps edges and units and turns the rest to faint glass; dusk lights
 * the `light` panels.
 */
export type Role = 'solid' | 'glass' | 'edge' | 'unit' | 'detail' | 'light';

/** A material the stage fades: by floor, by construction stage, by X-ray. */
export interface Faded {
  material: THREE.Material & { opacity: number };
  base: number;
  /** Glass never writes depth, or it hides what is behind it. */
  noDepth?: boolean;
  /** 1–4, see STAGES. */
  stage: number;
  role: Role;
  /** Raw concrete at the structure stage, its finish once the envelope is on. */
  finish?: { raw: THREE.Color; done: THREE.Color };
}

export interface UnitHandle {
  unit: SnapshotUnit;
  floorKey: string;
  /** Glow on select, hover and dusk. */
  materials: THREE.MeshStandardMaterial[];
  edge: THREE.LineBasicMaterial;
  label: CSS2DObject;
}

export interface FloorHandle {
  key: string;
  labelAr: string;
  order: number;
  height: number;
  /** Bottom of this storey when nothing is exploded. */
  stackedY: number;
  group: THREE.Group;
  faded: Faded[];
  tag: CSS2DObject;
  /** Current fade, 0 hidden … 1 solid. Animated by the stage. */
  presence: number;
  /** Every drawn corner of this storey, in world x/z: where its tag can sit. */
  corners: [number, number][];
  /** Centre and half-diagonal of this storey's own drawn extent, for isolation. */
  center: { x: number; z: number };
  radius: number;
}

export interface Building {
  root: THREE.Group;
  /** The top storey's lid, lifted off by explode and by isolation. */
  roof: { group: THREE.Group; faded: Faded[]; presence: number };
  floors: FloorHandle[];
  units: Map<string, UnitHandle>;
  pickable: THREE.Mesh[];
  /** Ceiling panels that light the interiors at dusk: warm, or mint where free. */
  lights: {
    material: THREE.MeshStandardMaterial;
    available: boolean;
    kind: 'panel' | 'floor' | 'led';
  }[];
  /** Each floor's glass, warmed from inside at dusk. */
  glazing: THREE.MeshStandardMaterial[];
  /** Half the footprint's diagonal, for framing. */
  radius: number;
  /** Half the drawing's width and depth, for placing the surroundings. */
  halfW: number;
  halfD: number;
  stackedHeight: number;
}

function prism(points: Point[], cx: number, cz: number, y0: number, depth: number) {
  const shape = new THREE.Shape(points.map(([x, y]) => new THREE.Vector2(x - cx, y - cz)));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  // Shape y → world z keeps the page's orientation seen from above; the
  // extrusion runs down −y after the turn, so lift it by its own depth.
  geometry.rotateX(Math.PI / 2);
  geometry.translate(0, y0 + depth, 0);
  return geometry;
}

/**
 * A ceiling seen only from below: from above (exploded, isolated) it culls
 * away and the room shows; from the street it is the lit soffit behind the glass.
 */
function ceiling(points: Point[], cx: number, cz: number, y: number) {
  const shape = new THREE.Shape(points.map(([x, z]) => new THREE.Vector2(x - cx, z - cz)));
  const geometry = new THREE.ShapeGeometry(shape);
  // +z normal → −y after the turn, with shape y → world z as in prism().
  geometry.rotateX(Math.PI / 2);
  geometry.translate(0, y, 0);
  return geometry;
}

function centroid(points: Point[]): Point {
  let a = 0;
  let x = 0;
  let y = 0;
  points.forEach((p, i) => {
    const q = points[(i + 1) % points.length]!;
    const cross = p[0] * q[1] - q[0] * p[1];
    a += cross;
    x += (p[0] + q[0]) * cross;
    y += (p[1] + q[1]) * cross;
  });
  return a === 0 ? points[0]! : [x / (3 * a), y / (3 * a)];
}

function tagElement(className: string, html: string) {
  const el = document.createElement('div');
  el.className = className;
  el.innerHTML = html;
  return el;
}

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** Columns on a 7.5 m grid inside the slab, clear of the cores. Illustrative. */
function columnGrid(outlines: Point[][], cores: Point[][]): Point[] {
  const all = outlines.flat();
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const spots: Point[] = [];
  for (let x = Math.min(...xs) + 1.2; x < Math.max(...xs); x += 7.5) {
    for (let y = Math.min(...ys) + 1.2; y < Math.max(...ys); y += 7.5) {
      const p: Point = [x, y];
      if (outlines.some((o) => inside(p, o)) && !cores.some((c) => inside(p, c))) spots.push(p);
    }
  }
  return spots;
}

export function buildBuilding(plan: Plan, units: SnapshotUnit[]): Building {
  const cx = plan.widthM / 2;
  const cz = plan.heightM / 2;
  const root = new THREE.Group();
  const byCode = new Map(units.map((u) => [`${u.floor}:${u.code}`, u]));
  const handles = new Map<string, UnitHandle>();
  const pickable: THREE.Mesh[] = [];
  const floors: FloorHandle[] = [];
  const lights: Building['lights'] = [];
  const glazing: THREE.MeshStandardMaterial[] = [];
  const roof = { group: new THREE.Group(), faded: [] as Faded[], presence: 1 };

  // Shared across floors: one canvas each.
  const stone = stoneTexture();
  const deck = deckTexture();
  const concrete = new THREE.Color(MAT.concrete);
  const white = new THREE.Color('#ffffff');

  let stackedY = 0;
  const ordered = [...plan.floors].sort((a, b) => a.order - b.order);

  ordered.forEach((floor: PlanFloor, index) => {
    const height = FLOOR_HEIGHTS_M[floor.key] ?? 4.2;
    const group = new THREE.Group();
    group.name = `floor:${floor.key}`;
    const faded: Faded[] = [];
    const ground = index === 0;

    const standard = (
      color: string,
      stage: number,
      role: Role,
      opacity = 1,
      extra: Partial<THREE.MeshStandardMaterial> = {},
    ) => {
      const material = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.8,
        metalness: 0,
        transparent: true,
        opacity,
        ...extra,
      });
      faded.push({ material, base: opacity, noDepth: extra.depthWrite === false, stage, role });
      return material;
    };
    const lines = (color: string, stage: number, opacity = 0.9, role: Role = 'edge') => {
      const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
      faded.push({ material, base: opacity, stage, role });
      return material;
    };
    // Scenery: drawn, shadowed, never picked, so a click passes through glass.
    const dress = (
      geometry: THREE.BufferGeometry | null,
      material: THREE.Material,
      cast = true,
    ) => {
      if (!geometry) return;
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = cast;
      mesh.receiveShadow = true;
      group.add(mesh);
    };
    const add = (
      geometry: THREE.BufferGeometry,
      material: THREE.Material,
      edge?: THREE.LineBasicMaterial,
      userData: Record<string, string> = {},
    ) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData = { floor: floor.key, ...userData };
      group.add(mesh);
      pickable.push(mesh);
      if (edge) group.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 25), edge));
      return mesh;
    };

    // ── Structure ──────────────────────────────────────────────────────────
    const slabStage = ground ? 1 : 2;
    const slabMaterial = standard(MAT.slab, slabStage, 'solid', 1, { roughness: 0.7 });
    const slabEdge = lines('#6d7880', slabStage, 0.35);
    // The cores go up as raw concrete and take their limestone with the envelope.
    const coreMaterial = standard('#ffffff', 2, 'solid', 1, { map: stone, roughness: 0.85 });
    faded[faded.length - 1]!.finish = { raw: concrete, done: white };
    const coreEdge = lines('#6d7880', 2, 0.3);

    const outlines = floor.context.filter((c) => c.kind === 'building').map((c) => c.points);
    const cores = floor.context.filter((c) => c.kind === 'core').map((c) => c.points);

    for (const shape of floor.context) {
      if (shape.kind === 'building') {
        add(prism(shape.points, cx, cz, 0, SLAB_M), slabMaterial, slabEdge);
      } else if (shape.kind === 'open') {
        add(
          prism(shape.points, cx, cz, 0, 0.12),
          standard(MAT.paving, 1, 'solid', 1, { roughness: 0.85 }),
        );
      } else if (shape.kind === 'water') {
        // The pool round cay: a stone basin holding shallow, bright water.
        add(
          prism(shape.points, cx, cz, 0.12, 0.06),
          standard('#5aa3b0', 1, 'detail', 0.85, { roughness: 0.05, metalness: 0.1 }),
        );
      } else if (shape.kind === 'core') {
        add(prism(shape.points, cx, cz, SLAB_M, height - SLAB_M), coreMaterial, coreEdge);
      }
      // `void` is an opening in the slab: nothing to draw.
    }

    const columnSpots = columnGrid(outlines, cores);
    if (columnSpots.length) {
      const columns = new THREE.InstancedMesh(
        new THREE.BoxGeometry(0.5, height - SLAB_M, 0.5),
        standard(MAT.concrete, 2, 'solid', 1, { roughness: 0.9 }),
        columnSpots.length,
      );
      const m = new THREE.Matrix4();
      columnSpots.forEach(([x, y], i) =>
        columns.setMatrixAt(i, m.makeTranslation(x - cx, SLAB_M + (height - SLAB_M) / 2, y - cz)),
      );
      columns.castShadow = true;
      columns.receiveShadow = true;
      group.add(columns);
    }

    // ── Envelope ───────────────────────────────────────────────────────────
    // Rooms are spaces and cores; terraces are open, so a room's edge on a
    // terrace is glazed, and a terrace's edge on a room has no planter wall.
    const rooms = [
      ...floor.spaces.filter((s) => s.kind !== 'terrace').map((s) => s.points),
      ...cores,
    ];
    const terraces = floor.spaces.filter((s) => s.kind === 'terrace').map((s) => s.points);
    const segs = (polys: Point[][]): Segment[] => polys.flatMap(edgesOf);
    const exterior = (points: Point[]) => runsOf(points, segs(rooms.filter((r) => r !== points)));
    const wall = { y0: SLAB_M, height: height - SLAB_M, cx, cz };

    const glazed = [
      ...floor.spaces.filter((s) => s.kind !== 'terrace').flatMap((s) => exterior(s.points)),
      ...outlines.flatMap((o) =>
        runsOf(o, segs([...rooms, ...terraces, ...outlines.filter((x) => x !== o)])),
      ),
    ];
    const curtain = curtainWall(glazed, wall);
    // Clear glass that reflects the sky, with the rooms behind it showing.
    const glass = standard(MAT.glass, 3, 'glass', 0.26, {
      roughness: 0.03,
      metalness: 0.4,
      depthWrite: false,
      emissive: new THREE.Color(MAT.warmLight),
      emissiveIntensity: 0,
    });
    glazing.push(glass);
    const charcoal = standard(MAT.charcoal, 3, 'solid', 1, { roughness: 0.5, metalness: 0.3 });
    dress(curtain.glass, glass, false);
    dress(curtain.frame, charcoal);
    dress(curtain.fascia, charcoal);
    dress(curtain.fins, standard(MAT.oak, 3, 'solid', 1, { map: deck, roughness: 0.6 }));
    // The night: LED under every fascia, and small lights along the planters.
    const led = standard('#fff3e0', 3, 'light', 1, {
      emissive: new THREE.Color(MAT.warmLight),
      emissiveIntensity: 0,
      roughness: 0.4,
    });
    lights.push({ material: led, available: false, kind: 'led' });
    dress(curtain.leds, led, false);

    const planterRuns = terraces.flatMap((t) => runsOf(t, segs(rooms)));
    const lampSpots: [number, number][] = [];
    for (const run of planterRuns) {
      for (let t = run.t0 + 0.8; t < run.t1 - 0.5; t += 1.8) {
        lampSpots.push([
          run.origin[0] + run.dir[0] * t + run.out[0] * 0.02 - cx,
          run.origin[1] + run.dir[1] * t + run.out[1] * 0.02 - cz,
        ]);
      }
    }
    if (lampSpots.length) {
      const lamps = new THREE.InstancedMesh(
        new THREE.SphereGeometry(0.07, 8, 6),
        led,
        lampSpots.length,
      );
      const m = new THREE.Matrix4();
      lampSpots.forEach(([x, z], i) =>
        lamps.setMatrixAt(i, m.makeTranslation(x, SLAB_M + 1.22, z)),
      );
      group.add(lamps);
    }
    dress(
      upstand(planterRuns, { y0: SLAB_M + 0.18, height: 1.0, cx, cz }, 0.5),
      standard(MAT.planter, 3, 'solid', 1, { roughness: 0.9 }),
    );

    // ── Fit-out ────────────────────────────────────────────────────────────
    group.add(
      shrubs(
        planterRuns,
        SLAB_M + 1.18,
        cx,
        cz,
        standard('#ffffff', 4, 'detail', 1, { roughness: 0.95 }),
      ),
    );
    const top = standard(MAT.tableTop, 4, 'detail', 1, { roughness: 0.55 });
    const chair = standard(MAT.chair, 4, 'detail', 1, { roughness: 0.7 });
    group.add(
      furniture(
        terraces.flatMap((t) => tableSpots(t)),
        SLAB_M + 0.18,
        cx,
        cz,
        {
          top,
          chair,
          canopy: standard(MAT.canopy, 4, 'detail', 1, { roughness: 0.9, side: THREE.DoubleSide }),
        },
      ),
    );

    const parents = new Map(floor.spaces.map((s) => [s.id, s]));
    const roomHeight = height - SLAB_M - 0.45;
    const interiorSpots: Point[] = [];
    // Shared per floor, not per room: every material is a shader to compile
    // before the first frame, and a phone pays for each one.
    const deckMaterial = standard('#ffffff', 4, 'detail', 1, { map: deck, roughness: 0.7 });
    const finishes = {
      let: standard(MAT.timberFloor, 4, 'detail', 1, {
        roughness: 0.6,
        emissive: new THREE.Color(MAT.warmLight),
        emissiveIntensity: 0,
      }),
      free: standard(MAT.emptyFloor, 4, 'detail', 1, {
        roughness: 0.6,
        emissive: new THREE.Color(MAT.availableGlow),
        emissiveIntensity: 0,
      }),
    };
    const panels = {
      let: standard('#f6f1e8', 4, 'light', 1, {
        emissive: new THREE.Color(MAT.warmLight),
        emissiveIntensity: 0,
      }),
      free: standard('#f6f1e8', 4, 'light', 1, {
        emissive: new THREE.Color(MAT.availableGlow),
        emissiveIntensity: 0,
      }),
    };
    lights.push(
      { material: panels.let, available: false, kind: 'panel' },
      { material: panels.free, available: true, kind: 'panel' },
      { material: finishes.let, available: false, kind: 'floor' },
      { material: finishes.free, available: true, kind: 'floor' },
    );

    for (const space of floor.spaces) {
      const ownerCode =
        space.kind === 'terrace' ? (parents.get(space.parentId ?? '')?.code ?? null) : space.code;
      const unit = ownerCode ? byCode.get(`${floor.key}:${ownerCode}`) : undefined;
      const available = unit?.status === 'available';

      if (space.kind === 'terrace') {
        const mesh = add(prism(space.points, cx, cz, SLAB_M, 0.18), deckMaterial, undefined, {
          ...(unit ? { code: unit.code } : {}),
        });
        mesh.castShadow = false;
        continue;
      }

      // Every room is furnished and lit, except a free one: that is an empty
      // shell, and its light is mint, so a free unit reads from across the bay.
      if (!available && space.kind !== 'office')
        interiorSpots.push(...tableSpots(space.points, 3, 1));
      const floorFinish = available ? finishes.free : finishes.let;
      add(prism(space.points, cx, cz, SLAB_M, 0.05), floorFinish, undefined, {
        ...(unit ? { code: unit.code } : {}),
      });
      dress(
        ceiling(space.points, cx, cz, height - 0.5),
        available ? panels.free : panels.let,
        false,
      );

      if (!unit) continue;

      let handle = handles.get(unit.code);
      if (!handle) {
        const label = new CSS2DObject(
          tagElement(
            `tag tag--unit ${available ? 'is-available' : 'is-leased'}`,
            `<i></i><span class="tag-pill"><b>${escapeHtml(unit.code)}</b><span>${
              available ? 'متاح' : escapeHtml(unit.brand ?? 'مؤجَّر')
            }</span></span>`,
          ),
        );
        // The anchor is the dot; the pill rides up and to the side on a leader.
        label.center.set(0, 1);
        label.element.dataset.code = unit.code;
        handle = {
          unit,
          floorKey: floor.key,
          materials: [],
          edge: lines(available ? MAT.available : '#8a8378', 4, available ? 0.95 : 0.35, 'unit'),
          label,
        };
        handles.set(unit.code, handle);
      }

      // The room's air: clear when leased (the furniture shows), a faint mint
      // when free. Pickable either way, so the whole room answers a click.
      const volume = standard(
        available ? MAT.available : '#ffffff',
        4,
        'unit',
        available ? 0.2 : 0.015,
        {
          roughness: 0.5,
          depthWrite: false,
        },
      );
      handle.materials.push(volume);
      add(prism(space.points, cx, cz, SLAB_M + 0.05, roomHeight), volume, handle.edge, {
        code: unit.code,
      });
      const [x, y] = centroid(space.points);
      handle.label.position.set(x - cx, SLAB_M + roomHeight * 0.6, y - cz);
      group.add(handle.label);

      // The sign on the fascia over the unit's longest outside wall.
      const text = available ? 'متاح للتأجير' : unit.brand;
      const longest = exterior(space.points).sort((a, b) => b.t1 - b.t0 - (a.t1 - a.t0))[0];
      if (text && longest && longest.t1 - longest.t0 >= 3) {
        const width = Math.min((longest.t1 - longest.t0) * 0.7, 9);
        const sign = signMesh(text, available, width);
        const mid = (longest.t0 + longest.t1) / 2;
        sign.position.set(
          longest.origin[0] + longest.dir[0] * mid + longest.out[0] * 0.43 - cx,
          height - 0.375,
          longest.origin[1] + longest.dir[1] * mid + longest.out[1] * 0.43 - cz,
        );
        sign.rotation.y = Math.atan2(longest.out[0], longest.out[1]);
        faded.push({
          material: sign.material as THREE.MeshBasicMaterial,
          base: 1,
          noDepth: true,
          stage: 4,
          role: 'detail',
        });
        group.add(sign);
      }
    }

    // Restaurant furniture inside the let and unlet-but-uncoded rooms.
    group.add(furniture(interiorSpots, SLAB_M + 0.05, cx, cz, { top, chair }));

    // ── Roof ───────────────────────────────────────────────────────────────
    // Not pickable: a click through it should reach the floor it covers.
    if (index === ordered.length - 1) {
      const roofFade = (
        material: THREE.Material & { opacity: number },
        stage: number,
        role: Role,
      ) => roof.faded.push({ material, base: material.opacity, stage, role });
      const material = new THREE.MeshStandardMaterial({
        color: MAT.slab,
        roughness: 0.85,
        transparent: true,
        opacity: 1,
      });
      const edge = new THREE.LineBasicMaterial({
        color: '#6d7880',
        transparent: true,
        opacity: 0.35,
      });
      roofFade(material, 2, 'solid');
      roofFade(edge, 2, 'edge');
      for (const shape of floor.context.filter((s) => s.kind === 'building')) {
        const geometry = prism(shape.points, cx, cz, 0, SLAB_M);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        roof.group.add(mesh, new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 25), edge));
      }
      // A charcoal-capped stone parapet round the outside of the roof.
      const parapetMaterial = new THREE.MeshStandardMaterial({
        map: stone,
        roughness: 0.85,
        transparent: true,
      });
      roofFade(parapetMaterial, 3, 'solid');
      const parapet = upstand(
        outlines.flatMap((o) => runsOf(o, segs(outlines.filter((x) => x !== o)))),
        { y0: SLAB_M, height: 1.0, cx, cz },
      );
      if (parapet) {
        const mesh = new THREE.Mesh(parapet, parapetMaterial);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        roof.group.add(mesh);
      }
      const plantParts = roofPlant(cores, SLAB_M, cx, cz);
      if (plantParts.length) {
        const plantMaterial = new THREE.MeshStandardMaterial({
          color: '#b9bcbf',
          roughness: 0.45,
          metalness: 0.6,
          transparent: true,
        });
        roofFade(plantMaterial, 4, 'detail');
        const plant = new THREE.Mesh(mergeGeometries(plantParts, false), plantMaterial);
        plant.castShadow = true;
        plant.receiveShadow = true;
        roof.group.add(plant);
      }
      roof.group.position.y = height;
      group.add(roof.group);
    }

    // The floor's name, for the exploded view; the stage places it each frame.
    const all = [...floor.context, ...floor.spaces].flatMap((s) => s.points);
    const xs = all.map((p) => p[0]);
    const zs = all.map((p) => p[1]);
    const [minX, maxX, minZ, maxZ] = [
      Math.min(...xs),
      Math.max(...xs),
      Math.min(...zs),
      Math.max(...zs),
    ];
    const tag = new CSS2DObject(
      tagElement('tag tag--floor', `<span>${escapeHtml(floor.labelAr)}</span>`),
    );
    tag.element.dataset.floor = floor.key;
    tag.position.set(maxX - cx + 4, height / 2, 0);
    group.add(tag);

    group.position.y = stackedY;
    root.add(group);
    floors.push({
      key: floor.key,
      labelAr: floor.labelAr,
      order: floor.order,
      height,
      stackedY,
      group,
      faded,
      tag,
      presence: 1,
      corners: all.map(([x, z]): [number, number] => [x - cx, z - cz]),
      center: { x: (minX + maxX) / 2 - cx, z: (minZ + maxZ) / 2 - cz },
      radius: Math.hypot(maxX - minX, maxZ - minZ) / 2,
    });
    stackedY += height;
  });

  return {
    root,
    roof,
    floors,
    units: handles,
    pickable,
    lights,
    glazing,
    radius: Math.hypot(plan.widthM, plan.heightM) / 2,
    halfW: plan.widthM / 2,
    halfD: plan.heightM / 2,
    stackedHeight: stackedY + SLAB_M,
  };
}
